import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader, Button } from '../ui-v2';
import {
  ToggleGroup, ToggleItem, KpiCard, Table, EmptyState, Tooltip, Badge, Modal, Drawer,
  Select, Input,
} from '../ui-v2.5';
import {
  MailPlus, UserPlus, Loader2, CircleDot, MailOpen, Inbox, CalendarClock, CheckCircle2,
  UserRound, Handshake, HelpCircle, AlertCircle, RefreshCw, Ban, AlertTriangle, Trash2, ShieldOff, Eye, Lock, X,
} from 'lucide-react';
import {
  listIncorporaciones, filtrosDelEmbudo,
  listNegocios, listPuestos, reenviarSolicitud, cancelarSolicitud, getSolicitud, borrarDatosCandidato,
  alcanceMio, listEmpresasColaboradoras, listEmpresasContratantes,
} from './api';
import IncorporacionesTabs from './IncorporacionesTabs';
import { UsuarioContext } from '../../providers/personaContext';
import { CambiosDeURL } from '../../../funciones/gestionURL';
import SolicitarDatosDrawer from './SolicitarDatosDrawer';
import WizardAlta from './WizardAlta';
import { rutaAlta } from './rutasProceso';
import CopiarEnlace from './components/CopiarEnlace';
import BotonExportarEmbudo from './BotonExportarEmbudo';
import { fmtFecha } from './fecha';

/**
 * Incorporaciones (Fase 8). El botón "Solicitar datos al candidato" abre el drawer de solicitud;
 * la tabla muestra las SOLICITUDES con su estado y las acciones de reenviar y cancelar. "Copiar
 * enlace" vive solo en el estado de éxito (drawer o reenvío), no como acción de fila: cerrado eso,
 * el enlace ya no existe. El wizard de alta y las etapas posteriores llegan en Fase 9.
 */

const cuerpoError = (e) => (e && e.response && e.response.data) || {};

const KPIS = [
  // `CircleDot`, no `Loader2`: el spinner significa "hay una petición en vuelo" y nada más. Aquí no
  // está animado, así que no engañaba, pero era el mismo DIBUJO para otro significado — y en el panel
  // de detalle esa misma confusión, ya con animación, hacía que una tarea en curso pareciese estar
  // cargando para siempre. Se unifica: "en curso" es un círculo con punto en todo el módulo.
  // (El catálogo de ui-v2.5 usa `Loader2` en su ejemplo de KpiCard; se aparta a propósito.)
  //
  // LAS CLAVES SON LAS DEL ESTADO, no un nombre propio de las tarjetas. Hasta el 06/10 la tarjeta
  // decía `enCurso` y el estado se llamaba `en_curso`: dos nombres para lo mismo, y por eso el
  // selector de estados habría sido un segundo filtro en vez del mismo. Ahora pulsar la tarjeta y
  // elegir en el selector escriben **el mismo valor en el mismo parámetro**.
  { key: 'en_curso', icon: CircleDot, label: 'En curso' },
  { key: 'esperando_datos', icon: MailOpen, label: 'Esperando datos' },
  { key: 'datos_recibidos', icon: Inbox, label: 'Datos recibidos', note: 'a completar' },
  { key: 'proximos30dias', icon: CalendarClock, label: 'Próximos 30 días' },
  { key: 'completadasMes', icon: CheckCircle2, label: 'Completadas mes' },
];

/* ── POR QUÉ AQUÍ NO HAY TARJETA DE «CANCELADAS» Y EN BAJAS SÍ ──────────────────────
 *
 * No es un olvido, y conviene que no se lea como tal.
 *
 * El problema de las canceladas era que **no se podía llegar a ellas**: ninguna tarjeta las
 * alcanzaba y no había selector. Eso lo resuelve el SELECTOR DE ESTADO, que las ofrece con su
 * recuento al lado. Una tarjeta sería una segunda forma de llegar al mismo sitio.
 *
 * Y las tarjetas no son un índice de estados: son **lo que hay que mirar hoy**. Una incorporación
 * cancelada no pide trabajo —no se avanza desde ahí—, así que ocuparía un hueco de los cinco que
 * se leen de un vistazo para decir un número sobre el que no se actúa.
 *
 * **En Bajas sí la tiene, y se queda.** Allí cancelar una baja significa que la persona vuelve a
 * estar activa —`cancelarBaja` la reactiva—, así que el número responde a una pregunta real:
 * cuántas salidas se echaron atrás. Aquí cancelar un alta no devuelve a nadie a ninguna parte.
 *
 * Misma palabra, dos cosas distintas. La asimetría está en los datos, no en las pantallas. */

// Estado del EMBUDO (clave del backend) → tono/etiqueta (leyenda del mockup).
const ESTADO_MAP = {
  esperando_datos: { tone: 'waiting', label: 'Esperando datos' },
  datos_recibidos: { tone: 'received', label: 'Datos recibidos' },
  en_curso: { tone: 'progress', label: 'En curso' },
  completada: { tone: 'active', label: 'Completada' },
  // Un alta que se está deshaciendo (pieza 5). El servidor lo emite desde la 5.1; sin esta línea
  // la fila caía al literal `cancelandose`.
  cancelandose: { tone: 'waiting', label: 'Cancelándose' },
  caducado: { tone: 'expired', label: 'Enlace caducado' },
  cancelada: { tone: 'expired', label: 'Cancelada' },
};
// Estado CRUDO de incorp_solicitudes (el del ciclo de vida, no el del embudo) → tono/etiqueta.
// Lo usa el drawer de detalle, que muestra la solicitud en sí y no su fila del embudo. Sin este
// mapa caía al literal de BBDD ('enviada', 'recibida'…) en el badge.
const ESTADO_RAW_MAP = {
  enviada: { tone: 'waiting', label: 'Enviada' },
  abierta: { tone: 'waiting', label: 'Abierta por el candidato' },
  recibida: { tone: 'received', label: 'Datos recibidos' },
  completada: { tone: 'active', label: 'Alta creada' },
  cancelada: { tone: 'expired', label: 'Cancelada' },
  caducada: { tone: 'expired', label: 'Enlace caducado' },
};
const TIPO_LABEL = { interno: 'Interno', colaborador: 'Colaborador' };
// Una solicitud está "cerrada" (sin reenvío/cancelación) si el candidato ya envió sus datos
// ('recibida'), si ya generó el alta ('completada') o si se canceló. `recibida` entra aquí porque
// el formulario público dejaba antes 'completada' en ese punto y el backend bloquea las dos igual.
const solicitudCerrada = (raw) => raw === 'recibida' || raw === 'completada' || raw === 'cancelada';
// Datos del candidato: existen desde que los envía y hasta que se borran a mano.
const conDatosDelCandidato = (raw) => raw === 'recibida' || raw === 'completada';
// Shim: adapta una fila del embudo (solicitud) a la forma que esperan los modales de solicitud.
// Propaga `tipo_persona`: el wizard lo necesita para fijar el tipo sin volver a preguntarlo.
const aSolicitud = (r) => ({
  id: r.id, nombre_candidato: r.nombre, tipo_persona: r.tipo_persona,
  estado: r.estado_raw, datos_borrados_en: r.datos_borrados_en,
});

const LEYENDA = [
  { tone: 'waiting', label: 'Esperando datos', desc: 'Enlace enviado, el candidato aún no ha enviado sus datos.' },
  { tone: 'received', label: 'Datos recibidos', desc: 'Formulario enviado. Falta que RRHH complete el alta (Fase 9).' },
  { tone: 'progress', label: 'En curso', desc: 'Alta creada. Áreas notificadas y tareas de onboarding en marcha (Fase 9).' },
  { tone: 'active', label: 'Completada', desc: 'Persona incorporada. Cuenta activa y todas las tareas hechas.' },
  { tone: 'expired', label: 'Enlace caducado', desc: 'El enlace expiró sin respuesta. Reenviar o cancelar.' },
];

export default function Incorporaciones() {
  /* ── LOS FILTROS VIVEN EN LA URL ───────────────────────────────────────────────────────
   *
   * No en `useState`: asi sobreviven al paginar, al volver atras y al compartir el enlace. Y se
   * escriben con `gestionURL`, que es la funcion que ya existe para esto —se arreglo el 01/10
   * precisamente para que admitiera parametros nuevos sin tocarla—. Un segundo manejo de URL
   * aqui seria la quinta copia de la semana.
   *
   * El AMBITO tambien: hasta hoy era `useState`, asi que cambiar de pestana y recargar te
   * devolvia a Internos. */
  const navigate = useNavigate();
  /* TODO cambio de filtro resetea la pagina. Quedarse en la 3 al filtrar deja una tabla vacia
   * que se lee como «no hay nada» y es «no hay pagina 3 con este filtro». */
  const cambiar = (clave) => (valor) => navigate(CambiosDeURL({ [clave]: valor, pagina: '' }));
  const irAPagina = (n) => navigate(CambiosDeURL({ pagina: n > 1 ? String(n) : '' }));
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [filas, setFilas] = useState([]);
  /* Lo que calcula el SERVIDOR sobre el conjunto entero. A `null` mientras no ha llegado: no es
   * «cero», es «todavia no se sabe», y pintar ceros mientras carga se lee como un embudo vacio. */
  const [resumenSrv, setResumenSrv] = useState(null);
  /* El catálogo de estados llega CON los datos. No se escribe aquí: una lista de opciones copiada
   * a mano es la que se queda atrás el día que se añada un estado —y no falla, sencillamente deja
   * de ofrecerlo—. */
  const [estadosSrv, setEstadosSrv] = useState([]);
  const [ordenSrv, setOrdenSrv] = useState(null);
  /* Pulsar una cabecera: misma columna invierte el sentido, otra columna empieza ascendente.
   * Y RESETEA LA PAGINA, como cualquier otro cambio de lo que se ve: quedarse en la 3 despues de
   * reordenar deja unas filas que no son ni las de antes ni las de ahora. */
  const columnaActual = ordenSrv ? ordenSrv.columna : null;
  const dirActual = ordenSrv ? ordenSrv.dir : 'asc';
  const ordenarPor = (clave) => navigate(CambiosDeURL({
    orden: clave,
    dir: clave === columnaActual && dirActual === 'asc' ? 'desc' : 'asc',
    pagina: '',
  }));

  const [contadoresSrv, setContadoresSrv] = useState(null);
  const [paginacion, setPaginacion] = useState(null);
  const [negocios, setNegocios] = useState([]);
  const [empresasColab, setEmpresasColab] = useState([]);
  const [empresasContratantes, setEmpresasContratantes] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [catalogosError, setCatalogosError] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [aReenviar, setAReenviar] = useState(null);
  const [aCancelar, setACancelar] = useState(null);
  const [aBorrarDatos, setABorrarDatos] = useState(null);
  const [aDetalle, setADetalle] = useState(null);
  const [aCompletar, setACompletar] = useState(null);   // solicitud a completar (§4.6)
  /* ── Un enlace a UN alta concreta: ahora es una RUTA ─────────────────────────────────────────
   *
   * El alta tiene pantalla propia (`AltaPage`, `…/incorporaciones/altas/:id`). Hasta el 14/10 se
   * abría aquí en un panel con `?alta=128`, y la pantalla BORRABA el parámetro al abrirlo: servía
   * para entrar, pero no decía qué había abierto.
   *
   * `?alta=` se sigue aceptando, para que no se rompa ningún enlace guardado: se REDIRIGE a la
   * ruta, con `replace` para que «atrás» no vuelva a pasar por aquí. Que exista lo comprueba la
   * pantalla del alta, que es la que sabe decir «no existe».
   */
  const [searchParams] = useSearchParams();
  const altaDeLaURL = searchParams.get('alta');
  useEffect(() => {
    if (altaDeLaURL) navigate(rutaAlta(altaDeLaURL), { replace: true });
  }, [altaDeLaURL, navigate]);

  const ambito = searchParams.get('ambito') === 'colaboradores' ? 'colaboradores' : 'internos';
  const fKpi = searchParams.get('kpi') || '';
  const fEmpresa = searchParams.get('empresa') || '';
  const fRazon = searchParams.get('razon_social') || '';
  const fDesde = searchParams.get('desde') || '';
  const fHasta = searchParams.get('hasta') || '';
  const fSinFecha = searchParams.get('sin_fecha') === '1';
  const pagina = Math.max(1, parseInt(searchParams.get('pagina'), 10) || 1);
  /* El orden vive en la URL como los filtros: sobrevive al paginar, al volver atras y al
   * compartir el enlace. Por defecto lo decide el SERVIDOR —hoy `fecha` ascendente, que es lo
   * mas cercano primero—, y la pantalla pinta lo que el servidor dice haber aplicado. */
  const fOrden = searchParams.get('orden') || '';
  const fDir = searchParams.get('dir') || '';
  // DOS cargas separadas, a propósito: el EMBUDO (solicitudes + altas) y los CATÁLOGOS de los selects
  // del drawer (negocios + puestos) tienen ciclos de vida distintos. Si el listado falla, los
  // desplegables siguen llenos; si los catálogos fallan, la tabla se sigue viendo. Cada uno con su
  // error. (Antes un único Promise.all: un 404 del listado vaciaba los desplegables.)
  /* LA ÚLTIMA PETICIÓN MANDA (18/10). `cargarEmbudo` lo lanzan los filtros y también cada acción
   * (crear, reenviar, cancelar…); si dos se cruzan, la respuesta de la vieja se descarta en vez de
   * pisar a la nueva. Antes se pintaba cualquier respuesta en el orden en que llegase. */
  const ultimaPeticion = useRef(0);
  const cargarEmbudo = useCallback(async () => {
    const mia = ++ultimaPeticion.current;
    const vigente = () => mia === ultimaPeticion.current;
    setCargando(true); setError(false);
    try {
      /* ── LOS FILTROS VAN AL SERVIDOR ─────────────────────────────────────────────────
       * Antes se pedia `{}` —siempre la pagina 1— y se filtraba aqui sobre esas 25 filas. Eso
       * no era «faltan filtros»: era que los KPI contaban sobre la pagina, y que **a partir de
       * la fila 26 el embudo no las ensenaba y no habia forma de llegar a ellas**. */
      /* Los filtros los arma `filtrosDelEmbudo` A PARTIR DE LA URL, que es la misma función que
       * usa el botón de exportar. Antes se armaban aquí a mano y allí de otra forma, y por eso el
       * fichero ignoraba la pestaña: dos sitios que podían separarse, y se separaron. */
      /* `filtrosDelEmbudo` ya trae tambien `orden` y `dir`: lo que pide la tabla y lo que pide
       * el Excel salen de la misma funcion, incluido el orden. */
      const res = await listIncorporaciones({
        ...filtrosDelEmbudo(searchParams.toString()),
        page: pagina,
      });
      if (!vigente()) return;
      const rows = Array.isArray(res?.incorporaciones) ? res.incorporaciones : [];
      /* El resumen, los contadores y la paginacion los calcula el SERVIDOR sobre el conjunto
       * entero. Recalcularlos aqui sobre la pagina es lo que los hacia mentir. */
      setResumenSrv(res?.resumen || null);
      setEstadosSrv(Array.isArray(res?.estados) ? res.estados : []);
      /* El orden que se pinta es el que el servidor dice haber APLICADO, no el que se pidio: una
       * flecha sacada de la peticion puede estar senalando una columna por la que no se ordeno. */
      setOrdenSrv(res?.orden || null);
      setContadoresSrv(res?.contadores || null);
      setPaginacion(res?.pagination || null);
      // rowKey único: solicitud id y alta id pueden colisionar → combinar tipo+id.
      setFilas(rows.map((r) => ({ ...r, _key: `${r.fila}-${r.id}` })));
    } catch (e) {
      if (!vigente()) return;
      console.error('[incorp][embudo] cargar', e);
      setError(true);
    } finally {
      if (vigente()) setCargando(false);
    }
    /* Las dependencias SON los filtros: sin ellas el `useCallback` se congela con los primeros
     * valores y cambiar un filtro no recargaria nada —la URL cambiaria y la tabla no—. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambito, fKpi, fEmpresa, fRazon, fDesde, fHasta, fSinFecha, fOrden, fDir, pagina]);

  const cargarCatalogos = useCallback(async () => {
    setCatalogosError(false);
    try {
      /* Lo nuevo va AL FINAL de la lista: insertarlo en medio corre todos los nombres de la
       * desestructuracion y deja a otro recibiendo lo que no es —paso el 02/10 con las empresas
       * contratantes de Perfiles—. */
      const [ns, ps, ec, em] = await Promise.all([
        listNegocios(), listPuestos(), listEmpresasColaboradoras(), listEmpresasContratantes(),
      ]);
      setNegocios(ns?.negocios || []);
      setPuestos(Array.isArray(ps) ? ps : []);
      setEmpresasColab(Array.isArray(ec) ? ec : []);
      setEmpresasContratantes(em?.empresas || []);
    } catch (e) {
      console.error('[incorp][catalogos] cargar', e);
      setCatalogosError(true);
    }
  }, []);

  /* DOS EFECTOS, como dice el comentario de arriba (18/10). Iban en uno, `[cargarEmbudo, cargarCatalogos]`,
   * y como `cargarEmbudo` cambia con cada filtro y cada página, los cuatro catálogos se volvían a pedir
   * con cada uno: el comentario decía «separadas» y se disparaban juntas. */
  useEffect(() => { cargarEmbudo(); }, [cargarEmbudo]);
  useEffect(() => { cargarCatalogos(); }, [cargarCatalogos]);



  /* ── YA NO SE FILTRA NI SE CUENTA AQUI ────────────────────────────────────────────────
   *
   * Hasta el 05/10 esta pantalla pedia la pagina 1 y filtraba y contaba sobre esas 25 filas,
   * haciendo DOS cosas mal a la vez —y la segunda era peor—:
   *
   *   · los KPI contaban sobre la pagina. El servidor YA los mandaba bien calculados sobre el
   *     conjunto y esta pantalla los tiraba para recalcularlos mal;
   *   · y **a partir de la fila 26 no habia forma de llegar a ellas**: sin control de paginacion
   *     y pidiendo siempre la pagina 1. Una tabla que parece completa y no lo esta.
   *
   * Lo que se ensena ES lo que el servidor devuelve, sin tocarlo. */
  const filtradas = filas;
  const contadores = {
    internos: contadoresSrv ? contadoresSrv.interno : 0,
    colaboradores: contadoresSrv ? contadoresSrv.colaborador : 0,
  };
  const resumen = resumenSrv || {};
  const nSinFecha = resumenSrv ? resumenSrv.sinFecha : 0;

  /* ── EL TIPO DE ALTA TAMBIEN SE ACOTA, Y AQUI ES DONDE SE ELIGE ───────────────────────
   *
   * El «selector de tipo» de un alta es ESTA pestaña: el wizard recibe `tipo` ya decidido. Así
   * que acotar el tipo no es tocar el wizard, es no ofrecer el botón en la pestaña que esa
   * persona no puede usar.
   *
   * Y eso es lo que hace que el caso malo DEJE DE EXISTIR en vez de dar un mensaje mejor: si
   * solo tiene puestos de colaborador, desde «Internos» no hay botón, no se abre el wizard, y no
   * se puede llegar a un desplegable de puestos vacío. Un camino sin salida que no se ofrece es
   * mejor que uno que se explica al final. */
  const [alcance, setAlcance] = useState(null);
  useEffect(() => {
    let cancel = false;
    alcanceMio()
      .then((a) => { if (!cancel) setAlcance(a); })
      /* Si falla NO se acota: degradar hacia MÁS información. El servidor sigue decidiendo. */
      .catch((e) => console.error('[incorp] alcanceMio', e));
    return () => { cancel = true; };
  }, []);

  /* ── VA AQUI, DESPUES DE `alcance`, Y NO ARRIBA CON `tipoAmbito` ───────────────────────
   *
   * Estuvo 29 lineas por encima de su propio `useState` y dejo la pantalla EN BLANCO en TEST:
   * `const` tiene zona muerta temporal, asi que leerlo antes de su declaracion lanza
   * «Cannot access 'alcance' before initialization» en CADA render y tumba el arbol entero.
   *
   * No lo caza nada de lo que usamos: el build compila —es un error de ejecucion, no de
   * sintaxis— y `no-use-before-define` esta configurada con `variables: false` en este
   * proyecto. Solo lo caza abrir la pantalla.
   *
   * `alcance` a null es «todavia no se sabe»: no se acota mientras carga, para no esconder el
   * boton y que aparezca despues —eso se lee como un fallo—. Y sin restriccion, todos. */
  /* ── CUALQUIER CONTROL QUE ACABE CREANDO UN ALTA MIRA EL ALCANCE ─────────────────────
   *
   * Enumeradas el 04/10, hay DOS puertas al wizard y ninguna mas: «Nueva incorporacion» y
   * «Completar alta» de una solicitud. `crearAltaIncorp` solo se llama desde `WizardAlta`, y
   * `WizardAlta` solo se monta en esos dos sitios. (La tercera, «Solicitar datos», crea una
   * SOLICITUD, no un alta.)
   *
   * Por eso esto es una funcion y no un booleano: la primera puerta pregunta por la pestana y
   * la segunda por el tipo de CADA solicitud, que puede no ser el de la pestana. Un booleano
   * habria servido a una y la otra habria vuelto a quedarse fuera —que es como llevamos tres
   * puertas en tres dias—. */
  const tipoEnAlcance = (t) => !alcance || !alcance.restringido
    || (alcance.tipos || []).includes(t);

  const COLUMNS = [
    { key: 'persona', sortable: true, header: 'Persona', render: (r) => (
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-v25-ink-900">{r.nombre}</span>
          {/* ── SOLICITUD Y ALTA SE PINTAN EN LA MISMA TABLA ───────────────────────────
              Y nada decia cual era cual. Con el recorte por alcance eso pasa de ser un
              detalle a ser una pregunta sin respuesta: quien ve tres filas de internos que
              no ha creado necesita saber que son SOLICITUDES —que no se recortan a
              proposito, para que dos personas no trabajen la misma sin verse—.
              La explicacion existia y era invisible. */}
          {r.fila === 'solicitud' && <Badge tone="received">Solicitud</Badge>}
        </div>
        <div className="truncate text-xs text-v25-ink-400">{r.fila === 'alta' ? r.numero : r.email}</div>
      </div>
    ) },
    { key: 'tipo', sortable: true, header: 'Tipo', render: (r) => <span className="text-sm">{TIPO_LABEL[r.tipo_persona] || r.tipo_persona}</span> },
    { key: 'puesto', sortable: true, header: 'Puesto', render: (r) => <span className="text-sm">{r.puesto || '—'}</span> },
    { key: 'fecha', sortable: true, header: 'Fecha prevista', render: (r) => <span className="text-sm">{fmtFecha(r.fecha)}</span> },
    { key: 'estado', sortable: true, header: 'Estado', render: (r) => {
      const m = ESTADO_MAP[r.estado] || { tone: 'info', label: r.estado };
      return <Badge tone={m.tone}>{m.label}</Badge>;
    } },
    { key: 'empresa', sortable: true, header: 'Empresa', render: (r) => <span className="text-sm">{r.empresa || '—'}</span> },
    { key: 'acciones', header: '', width: 260, render: (r) => {
      // ALTAS (Fase 10): abren el panel de seguimiento. Antes mostraban un guion.
      // Se abre desde este botón y no por clic en la fila: `Table` de ui-v2.5 no tiene `onRowClick`
      // y añadírselo sería tocar el DS. Mismo gesto que el detalle de solicitud.
      if (r.fila === 'alta') {
        return (
          <div className="flex items-center justify-end gap-1">
            <Tooltip content="Ver seguimiento del onboarding">
              <span>
                <Button variant="ghost" size="icon" onClick={() => navigate(rutaAlta(r.id))} aria-label="Ver seguimiento"><Eye /></Button>
              </span>
            </Tooltip>
          </div>
        );
      }
      if (r.fila !== 'solicitud') return <span className="block text-right text-xs text-v25-ink-300">—</span>;
      const puedeBorrar = conDatosDelCandidato(r.estado_raw) && !r.datos_borrados_en;
      const yaBorrado = !!r.datos_borrados_en;
      /* Completar una solicitud CREA UN ALTA, asi que mira el alcance ademas del estado.
       * Sin esto, alguien acotado a puestos de colaborador podia completar una solicitud de
       * interno: rellenaba el wizard entero y se llevaba un 403 al guardar. */
      const enMiAlcance = tipoEnAlcance(r.tipo_persona);
      const puedeCompletar = r.estado === 'datos_recibidos' && !r.datos_borrados_en && enMiAlcance;
      const tipBorrar = yaBorrado ? 'Datos ya borrados'
        : puedeBorrar ? 'Borrar datos del candidato'
        : 'Solo disponible cuando el candidato ha enviado sus datos';
      return (
        <div className="flex items-center justify-end gap-1">
          {puedeCompletar && (
            <Button size="sm" onClick={() => setACompletar(aSolicitud(r))} className="mr-1"><UserPlus />Completar alta</Button>
          )}
          {/* Se DICE por que no esta, igual que en la pestana: un boton que falta sin
              explicacion se lee como un fallo. */}
          {!enMiAlcance && r.estado === 'datos_recibidos' && !r.datos_borrados_en && (
            <Tooltip content={`Tus puestos asignados son de ${r.tipo_persona === 'interno' ? 'colaborador' : 'interno'}, así que no puedes completar esta solicitud.`}>
              <span className="mr-1 text-xs text-v25-ink-400">No puedes completarla</span>
            </Tooltip>
          )}
          <Tooltip content="Ver detalle">
            <span>
              <Button variant="ghost" size="icon" onClick={() => setADetalle(aSolicitud(r))} aria-label="Ver detalle"><Eye /></Button>
            </span>
          </Tooltip>
          <Tooltip content={solicitudCerrada(r.estado_raw) ? 'No disponible' : 'Reenviar enlace'}>
            <span>
              <Button variant="ghost" size="icon" disabled={solicitudCerrada(r.estado_raw)} onClick={() => setAReenviar(aSolicitud(r))} aria-label="Reenviar"><RefreshCw /></Button>
            </span>
          </Tooltip>
          <Tooltip content={solicitudCerrada(r.estado_raw) ? 'No disponible' : 'Cancelar solicitud'}>
            <span>
              <Button variant="ghost" size="icon" disabled={solicitudCerrada(r.estado_raw)} onClick={() => setACancelar(aSolicitud(r))} className="text-red-600 hover:bg-red-50 hover:text-red-700" aria-label="Cancelar"><Ban /></Button>
            </span>
          </Tooltip>
          <Tooltip content={tipBorrar}>
            <span>
              <Button variant="ghost" size="icon" disabled={!puedeBorrar} onClick={() => setABorrarDatos(aSolicitud(r))}
                className={puedeBorrar ? 'text-red-600 hover:bg-red-50 hover:text-red-700' : ''} aria-label="Borrar datos del candidato">
                {yaBorrado ? <ShieldOff /> : <Trash2 />}
              </Button>
            </span>
          </Tooltip>
        </div>
      );
    } },
  ];

  const cuerpoTabla = cargando ? (
    <div className="px-6 py-12 text-center text-sm text-v25-ink-400">
      <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
      Cargando…
    </div>
  ) : error ? (
    <div className="px-6 py-12 text-center">
      <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
      <p className="text-sm text-red-600">No se pudo cargar el listado. Inténtalo de nuevo más tarde.</p>
    </div>
  ) : (
    <EmptyState variant="minimal" title="Sin incorporaciones registradas" description="Solicita datos a un candidato o crea una incorporación." />
  );

  // El TIPO lo determina la pestaña activa, no un paso del wizard: en Internos ambos botones crean
  // internos; en Colaboradores, colaboradores. El drawer y el wizard lo reciben fijado.
  const tipoAmbito = ambito === 'internos' ? 'interno' : 'colaborador';
  /* `tipoEnAlcance` esta mas arriba porque lo usa `COLUMNS`; esto se queda AQUI porque depende de
   * `tipoAmbito`, que se declara en la linea de encima. Cada valor derivado, debajo de su fuente:
   * subir este con el otro «porque son del mismo tema» es justo lo que rompio la pantalla. */
  const puedeEsteTipo = tipoEnAlcance(tipoAmbito);

  /* ── LOS BOTONES, POR PERMISO ──────────────────────────────────────────────────────────
   *
   * La pantalla entera cuelga de `incorporaciones.ver`, pero CREAR y SOLICITAR son claves
   * aparte. Hasta el 03/10 los dos botones los veía cualquiera con `.ver`: se rellenaba el
   * wizard entero —doce campos, el DNI, el perfil— para llevarse un 403 al guardar.
   *
   * El servidor ya protegía; lo que faltaba era que la pantalla no ofreciera lo que el servidor
   * iba a rechazar. Es la misma familia que acotar el desplegable de puestos: **ofrecer algo
   * que va a ser rechazado no es permisivo, es hacer perder el trabajo**.
   *
   * Y los botones NO se deshabilitan: desaparecen. Un botón gris promete algo que nunca va a
   * poder pulsarse y manda a buscar qué falta; no estar no promete nada. Es el mismo criterio
   * que las columnas de contraseña, que tampoco se enmascaran.
   *
   * `permisosCargados` evita el parpadeo al entrar: sin él los botones aparecerían y se irían,
   * que se lee como un fallo. Super Administrador pasa por encima, como en todo el patrón. */

  const ctx = useContext(UsuarioContext) || {};
  const esSuperAdmin = (ctx.persona?.rol || '').toLowerCase() === 'super administrador';
  const permisosCargados = esSuperAdmin || ctx.permisosCargados;
  const puede = (clave) => esSuperAdmin
    || (Array.isArray(ctx.permisos) && ctx.permisos.includes(clave));

  const acciones = (
    <div className="flex items-center gap-2">
      {/* Exportar no lleva permiso propio: quien ve esta pantalla ya ve estas filas, y el fichero
          sale de las MISMAS —mismos filtros y mismo recorte por alcance—. Que columnas se lleva
          cada uno lo decide el servidor, no esta linea. */}
      <BotonExportarEmbudo total={paginacion ? paginacion.total : filtradas.length} />
      {permisosCargados && puede('incorporaciones.solicitar') && (
        <Button variant="outline" onClick={() => setDrawerOpen(true)}>
          <MailPlus />Solicitar datos al candidato
        </Button>
      )}
      {permisosCargados && puede('incorporaciones.crear') && puedeEsteTipo && (
        <Button onClick={() => setWizardOpen(true)}>
          <UserPlus />Nueva incorporación
        </Button>
      )}
      {permisosCargados && puede('incorporaciones.crear') && !puedeEsteTipo && (
        /* Se DICE por qué no está, en vez de dejar un hueco. Un botón que falta sin explicación
           se lee como un fallo de la pantalla, y manda a preguntar a Sistemas. */
        <span className="text-[13px] leading-relaxed text-v25-ink-500">
          Tus puestos asignados son de {tipoAmbito === 'interno' ? 'colaborador' : 'interno'}, así que
          aquí no puedes dar altas.
        </span>
      )}
    </div>
  );

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Incorporaciones"
        subtitle="Gestión de altas de personal: solicitud de datos, alta y credenciales."
        action={acciones}
      />

      <IncorporacionesTabs active="incorporaciones" />

      {/* ── UNA SOLA BARRA: el conmutador a la izquierda, los filtros a la derecha ───────
          Eran dos filas enteras y el aprovechamiento era malo. Ahora es una cuando cabe.

          `flex-wrap`, NO una fila fija. Es la leccion que ya esta escrita en el listado de
          personas: alli la barra era `nowrap`, entro un filtro mas, y el ultimo control **quedo
          cortado por la derecha** —y un boton recortado no se pulsa, y nadie lo denuncia porque
          parece que no esta—. Con `wrap` la barra crece a dos filas cuando hace falta y no se
          pierde ninguno.

          Tampoco se encogen los controles para que quepan: eso solo deja el problema para el
          filtro siguiente, y «Razon social» estrecha corta el nombre de la empresa, que es justo
          el dato por el que se filtra. */}
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {/* Cambiar de pestana RESETEA la pagina: quedarse en la 3 al cambiar de ambito deja una
            tabla vacia que parece «no hay nada» y es «no hay pagina 3 aqui». */}
        <ToggleGroup value={ambito} onChange={cambiar('ambito')}>
          <ToggleItem value="internos" label="Internos" icon={UserRound} count={contadores.internos} />
          <ToggleItem value="colaboradores" label="Colaboradores" icon={Handshake} count={contadores.colaboradores} />
        </ToggleGroup>
        <span className="text-xs text-v25-ink-400">
          Lo que crees desde aquí será <strong className="font-medium text-v25-ink-600">{tipoAmbito}</strong>.
        </span>

        {/* ── EL FILTRO QUE SE APAGA SOLO ────────────────────────────────────────────────
            Un número dice CUÁNTAS hay; un filtro te LLEVA a ellas. Por eso es un botón y no
            una etiqueta.
            Y sólo aparece si hay alguna: un filtro permanente que casi siempre da cero enseña
            a no mirarlo, y ocupa sitio en una barra que se lee de un vistazo. Cuando se
            resuelve la última, desaparece — y con él la pregunta. */}
        {nSinFecha > 0 && (
          <button
            type="button"
            onClick={() => cambiar('sin_fecha')(fSinFecha ? '' : '1')}
            aria-pressed={fSinFecha}
            title="Altas sin fecha de incorporación: esa persona no puede entrar y nadie la va a activar"
            className={`rounded-v25-md border px-2.5 py-1 text-xs font-medium transition ${
              fSinFecha
                ? 'border-amber-300 bg-amber-100 text-amber-900'
                : 'border-v25-line bg-white text-v25-ink-600 hover:bg-v25-ink-50'}`}
          >
            {nSinFecha} sin fecha de incorporación
          </button>
        )}
        {fSinFecha && (
          <span className="text-xs text-amber-700">
            Sin fecha no pasan a «Alta»: <strong className="font-medium">nadie las va a activar</strong>.
          </span>
        )}
      </div>

      {/* ── LOS FILTROS ────────────────────────────────────────────────────────────────
          «Empresa» significa cosas distintas segun la pestana —la marca para un interno, la
          empresa colaboradora para un colaborador— y por eso es UN selector cuyo contenido
          decide el ambito, y no tres mezclados.
          La RAZON SOCIAL va aparte, y solo en Internos: es otra pregunta —con quien se firma, la
          que RRHH mira para facturar— y un colaborador no la tiene. */}
      {/* `ml-auto` los pega a la derecha, como en el listado de personas; en movil el `wrap` los
          baja y el `ml-auto` deja de tener efecto. */}
      <div className="ml-auto flex flex-wrap items-end gap-2">
        <Select
          label={ambito === 'colaboradores' ? 'Empresa colaboradora' : 'Negocio'}
          value={fEmpresa}
          wrapperClassName="w-48"
          onChange={(e) => cambiar('empresa')(e.target.value)}
        >
          <option value="">Todas</option>
          {(ambito === 'colaboradores' ? empresasColab : negocios).map((x) => (
            <option key={x.id} value={x.id}>{x.nombre}</option>
          ))}
        </Select>
          {/* La ayuda pasa a ICONO, no se quita: en una sola fila no cabe debajo, y esta frase no
              sobra. Existe porque «empresa» significa tres cosas en este modulo y la confusion
              entre negocio y razon social ya costo un arreglo —el campo NO se explica solo: su
              nombre es justo el que se confunde—.

              La etiqueta se pinta A MANO y al `Select` no se le pasa `label`: el componente mete
              su etiqueta dentro de un `<label htmlFor>`, y un boton ahi dentro es HTML invalido
              —pulsarlo enfocaria el selector en vez de abrir la ayuda—.

              El `Tooltip` se abre tambien con `onFocus`, asi que esto no desaparece para quien
              navega con teclado. */}
        {ambito !== 'colaboradores' && (
          <div className="w-44">
            <span className="mb-1 flex items-center gap-1 text-xs font-medium text-v25-ink-700">
              Razón social
              <Tooltip content="Con quién se firma el contrato. No es la marca para la que trabaja.">
                <button type="button" aria-label="Qué es la razón social"
                  className="text-v25-ink-400 hover:text-v25-ink-600">
                  <HelpCircle className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
            </span>
          <Select aria-label="Razón social"
            value={fRazon} onChange={(e) => cambiar('razon_social')(e.target.value)}>
            <option value="">Todas</option>
            {empresasContratantes.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
          </Select>
          </div>
        )}
        {/* ── EL SELECTOR DE ESTADO ES EL MISMO FILTRO QUE LAS TARJETAS ────────────────
            Escribe `kpi`, igual que ellas, así que elegir aquí enciende la tarjeta y al revés. No
            son dos formas de hacer lo mismo: son dos puertas al mismo valor.

            Existe porque TRES estados no los alcanzaba ninguna tarjeta —`cancelada` (que ya la
            tiene), `caducado`, y `completada` fuera del mes en curso—. Medido el 05/10: 2 de 13
            filas en TEST vivían ahí, y la tarjeta «Completadas mes» decía 0 mientras había una
            completada en la tabla. */}
        {/* Sin ayuda: la que habia —«las tarjetas de arriba son atajos a este mismo filtro»— no
            explicaba el campo, describia algo que el usuario VE OCURRIR: al pulsar una tarjeta
            este selector cambia, y al elegir aqui se enciende la tarjeta. Una frase que cuenta lo
            que va a pasar delante de los ojos no hace falta. */}
        <Select label="Estado" value={fKpi} wrapperClassName="w-40"
          onChange={(e) => cambiar('kpi')(e.target.value)}>
          <option value="">Todos</option>
          {estadosSrv.map((x) => (
            <option key={x.clave} value={x.clave}>
              {x.etiqueta}{resumenSrv ? ` (${resumenSrv[x.clave] ?? 0})` : ''}
            </option>
          ))}
          {/* Dos de los ocho valores NO son estados —«Próximos 30 días» y «Completadas mes» son
              predicados sobre fechas—, así que no están en la lista. Si uno de ellos está activo,
              se añade aquí desactivado: sin esto el selector diría «Todos» mientras la tabla
              está filtrada, que es la pantalla contradiciéndose. */}
          {fKpi && !estadosSrv.some((x) => x.clave === fKpi) && (
            <option value={fKpi} disabled>
              {(KPIS.find((k) => k.key === fKpi) || {}).label || fKpi} (desde la tarjeta)
            </option>
          )}
        </Select>
        {/* LAS DOS FECHAS SON UN SOLO FILTRO, y por eso llevan una etiqueta y no dos: el
            servidor las trata como un rango —`desde` y `hasta` del mismo criterio— y leerlas como
            dos controles sueltos invita a poner solo una sin darse cuenta de que es un extremo. */}
        <div>
          <span className="mb-1 block text-xs font-medium text-v25-ink-700">Fecha prevista</span>
          <div className="flex items-center gap-1.5">
            <Input type="date" aria-label="Desde" value={fDesde}
              onChange={(e) => cambiar('desde')(e.target.value)} className="w-[9.5rem]" />
            <span className="text-v25-ink-400">–</span>
            <Input type="date" aria-label="Hasta" value={fHasta}
              onChange={(e) => cambiar('hasta')(e.target.value)} className="w-[9.5rem]" />
          </div>
        </div>
      </div>
      </div>

      {/* ── SOBRE CUÁNTAS SE CUENTAN ─────────────────────────────────────────
          Sin esto, quien pone un filtro no sabe si el número de la tarjeta habla del total o de lo
          que está viendo —y las dos lecturas son razonables—.

          VA DEBAJO DE LAS TARJETAS, no encima de los filtros. Nació arriba del todo, en el gris más
          claro de la página y separado de los números por cuatro selectores: estaba, se pintaba, y
          no lo vio nadie. Una frase que explica unos números tiene que tocarlos. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {KPIS.map((k) => (
          <KpiCard
            key={k.key}
            icon={k.icon}
            label={k.label}
            note={k.note}
            value={resumen[k.key] ?? 0}
            active={fKpi === k.key}
            /* Pulsar una tarjeta filtra por ella, y volver a pulsarla lo quita. El numero de la
               tarjeta PREDICE el total que sale: el mismo predicado cuenta y filtra, en el
               servidor y en un solo sitio. */
            onClick={() => cambiar('kpi')(fKpi === k.key ? '' : k.key)}
          />
        ))}
      </div>
      {resumenSrv && (
        <p className="-mt-1 text-xs text-v25-ink-500">
          Los recuentos son sobre las{' '}
          <strong className="font-semibold text-v25-ink-700">{resumenSrv.sobre}</strong>
          {' '}de este filtro, y no cambian al pulsar una tarjeta: si cambiaran, pulsar una dejaría las
          demás a cero.
        </p>
      )}

      <Table
        columns={COLUMNS}
        rows={filtradas}
        rowKey="_key"
        emptyState={cuerpoTabla}
        sortBy={columnaActual}
        sortDir={dirActual}
        onSort={ordenarPor}
      />

      {/* ── LA PAGINACION, QUE NO EXISTIA ──────────────────────────────────────────────
          Hasta el 05/10 esta pantalla pedia siempre la pagina 1 y no tenia control: **a partir
          de la fila 26 no habia forma de llegar a las demas**, y la tabla terminaba como si no
          hubiera mas. Con 0 filas en produccion y 10 en TEST nadie lo habia visto. */}
      {paginacion && paginacion.totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="text-v25-ink-500">
            {paginacion.total} en total · página {paginacion.page} de {paginacion.totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={paginacion.page <= 1}
              onClick={() => irAPagina(paginacion.page - 1)}>Anterior</Button>
            <Button variant="outline" size="sm" disabled={paginacion.page >= paginacion.totalPages}
              onClick={() => irAPagina(paginacion.page + 1)}>Siguiente</Button>
          </div>
        </div>
      )}

      <details className="rounded-v25-lg border border-v25-line bg-white px-4 py-3">
        <summary className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-v25-ink-500">
          <HelpCircle className="h-3.5 w-3.5" />
          Leyenda de estados
        </summary>
        <div className="mt-3 grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
          {LEYENDA.map((l) => (
            <div key={l.tone} className="flex items-start gap-2">
              <Badge tone={l.tone} className="mt-0.5 shrink-0">{l.label}</Badge>
              <span className="text-v25-ink-500">{l.desc}</span>
            </div>
          ))}
        </div>
      </details>

      <SolicitarDatosDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        tipo={tipoAmbito}
        negocios={negocios}
        puestos={puestos}
        catalogosError={catalogosError}
        onReintentarCatalogos={cargarCatalogos}
        onCreada={cargarEmbudo}
      />

      {wizardOpen && (
        <WizardAlta
          open={wizardOpen}
          tipo={tipoAmbito}
          onClose={() => setWizardOpen(false)}
          onCreada={cargarEmbudo}
        />
      )}

      {aCompletar && (
        <WizardAlta
          open
          solicitud={aCompletar}
          onClose={() => setACompletar(null)}
          onCreada={() => { setACompletar(null); cargarEmbudo(); }}
        />
      )}

      {aReenviar && (
        <ReenviarModal
          solicitud={aReenviar}
          onClose={() => setAReenviar(null)}
          onReenviado={cargarEmbudo}
        />
      )}

      {aCancelar && (
        <CancelarModal
          solicitud={aCancelar}
          onClose={() => setACancelar(null)}
          onCancelado={() => { setACancelar(null); cargarEmbudo(); }}
        />
      )}

      {aBorrarDatos && (
        <BorrarDatosModal
          solicitud={aBorrarDatos}
          onClose={() => setABorrarDatos(null)}
          onBorrado={() => { setABorrarDatos(null); cargarEmbudo(); }}
        />
      )}

      {aDetalle && (
        <DetalleSolicitudDrawer
          solicitud={aDetalle}
          onClose={() => setADetalle(null)}
        />
      )}

      {/* El seguimiento del alta ya no se abre aquí: tiene su ruta (`AltaPage`). Al volver, el
          embudo se monta de nuevo y carga, así que el progreso y el KPI salen al día sin `onCambio`. */}
    </div>
  );
}

// ─────────────────────────── Reenviar (con aviso si el candidato ya entró) ───────────────────────────
function ReenviarModal({ solicitud, onClose, onReenviado }) {
  const [procesando, setProcesando] = useState(false);
  const [exito, setExito] = useState(null); // { enlace }
  const abierta = solicitud.estado === 'abierta';

  async function confirmar() {
    setProcesando(true);
    try {
      const res = await reenviarSolicitud(solicitud.id);
      toast.success('Enlace reenviado. El anterior queda invalidado.');
      setExito({ enlace: res.enlace });
      if (onReenviado) onReenviado();
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudo reenviar.');
    } finally {
      setProcesando(false);
    }
  }

  const footer = exito ? (
    <Button onClick={onClose}>Hecho</Button>
  ) : (
    <>
      <Button variant="outline" onClick={onClose} disabled={procesando}>Cancelar</Button>
      <Button onClick={confirmar} disabled={procesando}>
        {procesando ? <Loader2 className="animate-spin" /> : <RefreshCw />}Reenviar enlace
      </Button>
    </>
  );

  return (
    <Modal open onClose={onClose} size="md" tone={abierta && !exito ? 'danger' : undefined}
      title="Reenviar solicitud" subtitle={solicitud.nombre_candidato} footer={footer}>
      {exito ? (
        <div className="space-y-3">
          <p className="text-sm text-v25-ink-700">Nuevo enlace generado. El anterior ya no funciona.</p>
          <CopiarEnlace enlace={exito.enlace} />
        </div>
      ) : (
        <div className="space-y-3">
          {abierta && (
            <div className="flex items-start gap-2 rounded-v25-md border border-v25-expired-bd bg-v25-expired-bg p-3 text-xs text-v25-expired-fg">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                El candidato <strong>ya abrió</strong> el formulario. Reenviar genera un enlace nuevo e
                <strong> invalida el actual</strong>: como el formulario no guarda nada hasta el envío,
                perderá lo que llevara escrito.
              </span>
            </div>
          )}
          <p className="text-sm text-v25-ink-700">
            Se generará un enlace nuevo con una caducidad renovada y el anterior dejará de servir. ¿Continuar?
          </p>
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────── Cancelar ───────────────────────────
function CancelarModal({ solicitud, onClose, onCancelado }) {
  const [procesando, setProcesando] = useState(false);
  async function confirmar() {
    setProcesando(true);
    try {
      await cancelarSolicitud(solicitud.id);
      toast.success('Solicitud cancelada.');
      onCancelado();
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudo cancelar.');
    } finally {
      setProcesando(false);
    }
  }
  return (
    <Modal open onClose={onClose} size="sm" tone="danger" title="Cancelar solicitud" subtitle={solicitud.nombre_candidato}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={procesando}>Volver</Button>
          <Button variant="destructive" onClick={confirmar} disabled={procesando}>
            {procesando ? <Loader2 className="animate-spin" /> : <Ban />}Cancelar solicitud
          </Button>
        </>
      )}>
      <p className="text-sm text-v25-ink-700">
        El enlace dejará de funcionar y el candidato no podrá enviar sus datos. Esta acción no se puede deshacer
        (tendrías que crear una solicitud nueva). ¿Seguro?
      </p>
    </Modal>
  );
}

// ─────────────────────────── Borrar datos del candidato (danger) ───────────────────────────
function BorrarDatosModal({ solicitud, onClose, onBorrado }) {
  const [procesando, setProcesando] = useState(false);
  const [resumen, setResumen] = useState(null);   // recuentos reales (sin valores)
  const [cargandoResumen, setCargandoResumen] = useState(true);

  useEffect(() => {
    let cancel = false;
    getSolicitud(solicitud.id)
      .then((d) => { if (!cancel) setResumen(d.datos_resumen || null); })
      .catch(() => { /* si falla, seguimos: el texto genérico basta */ })
      .finally(() => { if (!cancel) setCargandoResumen(false); });
    return () => { cancel = true; };
  }, [solicitud.id]);

  async function confirmar() {
    setProcesando(true);
    try {
      const res = await borrarDatosCandidato(solicitud.id);
      const b = res.blobs || {};
      toast.success(`Datos borrados.${b.total ? ` Fotos del DNI: ${b.borrados}/${b.total}${b.fallidos ? ` (${b.fallidos} ya no existían)` : ''}.` : ''}`);
      onBorrado();
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudieron borrar los datos.');
    } finally {
      setProcesando(false);
    }
  }

  // Lista de lo que se pierde, con recuentos reales cuando los hay.
  const items = [];
  if (resumen) {
    items.push('Datos personales, dirección y datos de RRHH');
    if (resumen.iban) items.push('IBAN');
    if (resumen.num_seguridad_social) items.push('Nº de la Seguridad Social');
    if (resumen.documentos) items.push(`${resumen.documentos} foto(s) del DNI`);
    if (resumen.hijos) items.push(`${resumen.hijos} hijo(s) declarado(s)`);
    if (resumen.titulaciones) items.push(`${resumen.titulaciones} titulación(es)`);
  }

  return (
    <Modal open onClose={onClose} size="sm" tone="danger" title="Borrar datos del candidato" subtitle={solicitud.nombre_candidato}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={procesando}>Volver</Button>
          <Button variant="destructive" onClick={confirmar} disabled={procesando || cargandoResumen}>
            {procesando ? <Loader2 className="animate-spin" /> : <Trash2 />}Borrar datos
          </Button>
        </>
      )}>
      <div className="space-y-3">
        <p className="text-sm text-v25-ink-700">
          Se eliminarán <strong>los datos aportados por el candidato, incluidas las fotos del DNI</strong>,
          <strong> sin posibilidad de recuperarlos</strong>.
        </p>
        {cargandoResumen ? (
          <p className="flex items-center gap-1 text-xs text-v25-ink-500"><Loader2 className="h-3 w-3 animate-spin" /> Calculando qué se borrará…</p>
        ) : items.length ? (
          <ul className="list-disc space-y-0.5 pl-5 text-sm text-v25-ink-700">
            {items.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        ) : null}
        <p className="text-xs text-v25-ink-500">
          La solicitud <strong>se conserva</strong> con su rastro (quién la envió, cuándo, a qué correo y su estado);
          solo se borran los datos personales.
        </p>
      </div>
    </Modal>
  );
}

// ─────────────────────────── Detalle de la solicitud (solo NO confidencial) ───────────────────────────
function Campo({ label, children }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-v25-ink-400">{label}</dt>
      <dd className="text-sm text-v25-ink-800">{children || <span className="text-v25-ink-400">—</span>}</dd>
    </div>
  );
}

function DetalleSolicitudDrawer({ solicitud, onClose }) {
  const [d, setD] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancel = false;
    setCargando(true); setError(false);
    getSolicitud(solicitud.id)
      .then((res) => { if (!cancel) setD(res); })
      .catch(() => { if (!cancel) setError(true); })
      .finally(() => { if (!cancel) setCargando(false); });
    return () => { cancel = true; };
  }, [solicitud.id]);

  const est = d && (ESTADO_RAW_MAP[d.estado] || { tone: 'info', label: d.estado });
  const det = d && d.datos_detalle;
  const res = d && d.datos_resumen;

  const header = (
    <div className="flex items-center justify-between border-b border-v25-line px-5 py-4">
      <div className="min-w-0">
        <h2 className="truncate text-sm font-semibold text-v25-ink-900">
          {d ? [d.nombre_candidato, d.apellidos_candidato].filter(Boolean).join(' ') : solicitud.nombre_candidato}
        </h2>
        <p className="truncate text-xs text-v25-ink-500">Detalle de la solicitud</p>
      </div>
      <button type="button" onClick={onClose} className="text-v25-ink-400 hover:text-v25-ink-700" aria-label="Cerrar"><X className="h-5 w-5" /></button>
    </div>
  );

  return (
    <Drawer open onClose={onClose} variant="brand" width={560} header={header}>
      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-v25-ink-500"><Loader2 className="h-5 w-5 animate-spin" /> Cargando…</div>
      ) : error ? (
        <div className="px-5 py-12 text-center text-sm text-red-600"><AlertCircle className="mx-auto mb-2 h-6 w-6" />No se pudo cargar el detalle.</div>
      ) : (
        <>
          {/* Solicitud */}
          <section className="rounded-v25-md border border-v25-line bg-white p-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Solicitud</h3>
            <dl className="grid grid-cols-2 gap-3">
              <Campo label="Email">{d.candidato ? d.candidato.email : d.email_candidato}</Campo>
              <Campo label="Estado">{est && <Badge tone={est.tone}>{est.label}</Badge>}</Campo>
              <Campo label="Negocio">{d.empresa}</Campo>
              <Campo label="Puesto">{d.puesto}</Campo>
              <Campo label="Tipo">{d.tipo_persona === 'colaborador' ? 'Colaborador' : 'Interno'}</Campo>
              <Campo label="Fecha prevista">{fmtFecha(d.fecha_incorporacion_tentativa)}</Campo>
              <Campo label="Enviada">{fmtFecha(d.enviada_en)}</Campo>
              <Campo label="Recibida">{fmtFecha(d.recibida_en)}</Campo>
            </dl>
            {Array.isArray(d.avisos) && d.avisos.length > 0 && (
              <div className="mt-3">
                <dt className="text-[11px] font-medium uppercase tracking-wide text-v25-ink-400">Avisos</dt>
                <div className="mt-1 flex flex-wrap gap-1">
                  {d.avisos.map((a) => <span key={a.id} className="rounded-v25-full bg-v25-surface-sunken px-2 py-0.5 text-xs text-v25-ink-600">{a.email}</span>)}
                </div>
              </div>
            )}
          </section>

          {/* Datos del candidato */}
          <section className="rounded-v25-md border border-v25-line bg-white p-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Datos del candidato</h3>
            {d.datos_borrados_en ? (
              <div className="flex items-start gap-2 rounded-v25-md border border-v25-line bg-v25-surface-sunken p-3 text-xs text-v25-ink-500">
                <ShieldOff className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Datos borrados el <strong>{fmtFecha(d.datos_borrados_en)}</strong>. La solicitud se conserva por su auditoría.</span>
              </div>
            ) : !det ? (
              <p className="text-sm text-v25-ink-400">El candidato aún no ha enviado sus datos.</p>
            ) : (
              <>
                <dl className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Campo label="Dirección">{det.direccion}</Campo></div>
                  <Campo label="Código postal">{det.codigo_postal}</Campo>
                  <Campo label="Población">{det.poblacion}</Campo>
                  <Campo label="Provincia">{det.provincia}</Campo>
                  <Campo label="Teletrabajo">{det.direccion_teletrabajo}</Campo>
                  <Campo label="Lugar de nacimiento">{det.lugar_nacimiento}</Campo>
                  <Campo label="Fecha de nacimiento">{fmtFecha(det.fecha_nacimiento)}</Campo>
                  <Campo label="Teléfono personal">{det.telefono_personal}</Campo>
                  <Campo label="Estado civil">{det.estado_civil}</Campo>
                  <Campo label="Nº de hijos">{det.hijos_count}</Campo>
                </dl>

                {det.titulaciones && det.titulaciones.length > 0 && (
                  <div className="mt-3">
                    <dt className="mb-1 text-[11px] font-medium uppercase tracking-wide text-v25-ink-400">Titulaciones</dt>
                    <ul className="divide-y divide-v25-line rounded-v25-md border border-v25-line">
                      {det.titulaciones.map((t, i) => (
                        <li key={i} className="px-3 py-2 text-sm text-v25-ink-800">
                          {t.titulo || 'Titulación'}{t.tipo ? <span className="text-v25-ink-500"> · {t.tipo}</span> : null}
                          {t.centro || t.anio ? <div className="text-xs text-v25-ink-500">{[t.centro, t.anio].filter(Boolean).join(' · ')}</div> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Confidenciales: solo PRESENCIA. Se consultan en la ficha de la persona con datos_rrhh.ver. */}
                {res && (
                  <div className="mt-4 rounded-v25-md border border-v25-line bg-v25-surface-sunken p-3">
                    <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-v25-ink-600">
                      <Lock className="h-3.5 w-3.5" /> Datos confidenciales (no se muestran aquí)
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="rounded-v25-full bg-white px-2 py-0.5 text-v25-ink-600 ring-1 ring-v25-line">IBAN: {res.iban ? 'presente' : '—'}</span>
                      <span className="rounded-v25-full bg-white px-2 py-0.5 text-v25-ink-600 ring-1 ring-v25-line">Nº Seguridad Social: {res.num_seguridad_social ? 'presente' : '—'}</span>
                      <span className="rounded-v25-full bg-white px-2 py-0.5 text-v25-ink-600 ring-1 ring-v25-line">Fotos DNI: {res.documentos || 0}</span>
                    </div>
                    <p className="mt-2 text-[11px] text-v25-ink-400">
                      Se consultan en la ficha de la persona, que requiere el permiso de datos de RRHH. Esta pantalla no lo exige.
                    </p>
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}
    </Drawer>
  );
}
